package com.arenaproplay.admin.ui.layouts.shell

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.core.config.AppConfig
import com.arenaproplay.admin.core.config.Environment
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.state.auth.LocalAuthSession
import com.arenaproplay.admin.ui.localization.LocalAppStrings
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.sBorderRadius
import com.arenaproplay.admin.ui.utils.sPadding
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*

/** Health endpoint is at the API root, NOT under /api. */
private const val HEALTH_URL = "https://api.arenaproplay.com/health"
private const val HEALTH_URL_DEV = "http://localhost:8080/health"
private const val LATENCY_WARN_MS = 500

/**
 * Night Arena v2 topbar — slim dark glass bar.
 *
 * Since the sidebar is now an icon rail without labels, the topbar shows
 * the current **section name** in Unbounded 15 px + an optional subtitle
 * slot.  The old "Group / Page" breadcrumb is replaced by the section label
 * alone; the icon rail makes the hierarchy self-evident.
 *
 * Right side: ⌘K hint chip · latency pill · env badge · user chip+dropdown.
 *
 * Mobile (<920px): hamburger button (left) + section title (center) + avatar chip (right).
 * Desktop topbar is unchanged (menu items hidden via media query on mobile).
 */
@Composable
fun Topbar(
    title: String,
    subtitle: String?,
    onToggleSidebar: () -> Unit,
    onHamburger: (() -> Unit)? = null,
    actions: (@Composable () -> Unit)? = null
) {
    val ctx = rememberPageContext()
    val session = LocalAuthSession.current
    val strings = LocalAppStrings.current
    var menuOpen by remember { mutableStateOf(false) }

    val currentPath = ctx.route.path
    // Resolve the section name from the nav map; fall back to the page title.
    val sectionLabel = breadcrumbFor(currentPath)?.second ?: title

    Box(
        Modifier
            .fillMaxWidth()
            .styleModifier {
                property("background", "rgba(5,8,15,0.88)")
                property("border-bottom", "1px solid rgba(255,255,255,.06)")
                property("backdrop-filter", "blur(10px)")
                property("-webkit-backdrop-filter", "blur(10px)")
                property("position", "sticky")
                property("top", "0")
                property("z-index", "200")
            }
    ) {
        Row(
            Modifier
                .fillMaxWidth()
                .padding(leftRight = 22.px, topBottom = 12.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // ── Mobile hamburger (hidden on desktop via CSS) ──────────────────
            Box(Modifier.classNames("na-mobile-hamburger")) {
                Box(
                    Modifier
                        .width(44.px).height(44.px)
                        .borderRadius(50.percent)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property("background", "rgba(255,255,255,.06)")
                            property("border", "1px solid rgba(255,255,255,.08)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("transition", "background 140ms ease")
                            property("flex-shrink", "0")
                        }
                        .classNames("na-press")
                        .onClick { onHamburger?.invoke() },
                    contentAlignment = Alignment.Center
                ) {
                    // Three-line hamburger icon via inline SVG
                    Box(
                        Modifier.styleModifier {
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("gap", "4px")
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                    ) {
                        repeat(3) {
                            Box(
                                Modifier
                                    .styleModifier {
                                        property("width", "16px")
                                        property("height", "1.5px")
                                        property("background", "rgba(240,244,252,.80)")
                                        property("border-radius", "2px")
                                    }
                            )
                        }
                    }
                }
                Box(Modifier.width(12.px))
            }

            // ── Section name (left / center on mobile) ───────────────────────
            Box(Modifier.weight(1f)) {
                Column {
                    SpanText(
                        sectionLabel,
                        Modifier.styleModifier {
                            property("font-family", "'Unbounded', sans-serif")
                            property("font-size", "15px")
                            property("font-weight", "600")
                            property("color", "#F0F4FC")
                            property("line-height", "1.2")
                            property("letter-spacing", "-0.02em")
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                    )
                    if (subtitle != null) {
                        Box(Modifier.height(2.px))
                        SpanText(
                            subtitle,
                            Modifier.classNames("na-topbar-subtitle").styleModifier {
                                property("font-size", "12px")
                                property("color", "rgba(240,244,252,.45)")
                                property("white-space", "nowrap")
                            }
                        )
                    }
                }
            }

            // ── Desktop right-side actions (hidden on mobile) ─────────────────
            Row(Modifier.classNames("na-topbar-actions", "na-desktop-topbar-right"), verticalAlignment = Alignment.CenterVertically) {
                if (actions != null) {
                    actions()
                    Box(Modifier.width(12.px))
                }

                // ── ⌘K hint chip ──────────────────────────────────────────────
                Box(Modifier.classNames("na-cmdk-chip"), contentAlignment = Alignment.Center) {
                CmdKChip()
                }
                Box(Modifier.width(8.px))

                // ── API latency pill ──────────────────────────────────────────
                LatencyPill()
                Box(Modifier.width(8.px))

                // ── Env badge ─────────────────────────────────────────────────
                EnvBadge()

                Box(Modifier.width(12.px))

                // ── Divider ───────────────────────────────────────────────────
                Box(
                    Modifier.width(1.px).height(18.px)
                        .styleModifier { property("background", "rgba(255,255,255,.08)") }
                )
                Box(Modifier.width(12.px))

                // ── User chip + dropdown ───────────────────────────────────────
                Box {
                    UserChip(
                        name = session.user?.name ?: session.user?.email ?: "Admin",
                        role = session.user?.role ?: "ADMIN",
                        onClick = { menuOpen = !menuOpen }
                    )
                    if (menuOpen) {
                        UserMenu(
                            onSettings = { menuOpen = false; ctx.router.navigateTo(Routes.SETTINGS) },
                            onLogout = {
                                menuOpen = false
                                session.signOut()
                                ctx.router.navigateTo(Routes.LOGIN)
                            },
                            settingsLabel = strings.navSettings,
                            logoutLabel = strings.logout
                        )
                    }
                }
            }

            // ── Mobile avatar chip (visible only on mobile) ───────────────────
            Box(Modifier.classNames("na-mobile-avatar-chip")) {
                val name = session.user?.name ?: session.user?.email ?: "A"
                Box(
                    Modifier
                        .width(36.px).height(36.px)
                        .borderRadius(50.percent)
                        .styleModifier { property("background", AppColors.AvatarGradient) },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        name.firstOrNull()?.uppercaseChar()?.toString() ?: "A",
                        Modifier.styleModifier {
                            property("font-size", "13px")
                            property("font-weight", "600")
                            property("color", "#FFFFFF")
                        }
                    )
                }
            }
        }
    }
}

// ── ⌘K hint chip ─────────────────────────────────────────────────────────────

@Composable
private fun CmdKChip() {
    Row(
        Modifier
            .padding(leftRight = 7.px, topBottom = 3.px)
            .sBorderRadius(6)
            .styleModifier {
                property("background", "rgba(255,255,255,.05)")
                property("border", "1px solid rgba(255,255,255,.08)")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            "⌘K",
            Modifier.styleModifier {
                property("font-family", "'JetBrains Mono', monospace")
                property("font-size", "10.5px")
                property("font-weight", "500")
                property("color", "rgba(240,244,252,.35)")
                property("letter-spacing", "0")
            }
        )
    }
}

// ── API latency pill ────────────────────────────────────────────────────────

private data class LatencyState(val ms: Int?, val ok: Boolean)

@Composable
private fun LatencyPill() {
    var latency by remember { mutableStateOf<LatencyState?>(null) }
    val healthUrl = if (AppConfig.environment == Environment.DEVELOPMENT) HEALTH_URL_DEV else HEALTH_URL

    LaunchedEffect(healthUrl) {
        while (true) {
            val result = pingHealth(healthUrl)
            latency = result
            delay(60_000)
        }
    }

    val state = latency ?: return
    val ms = state.ms
    val ok = state.ok
    val label = if (ms != null) "API · $ms ms" else "API · ERR"
    val (bg, fg, dot) = if (ok && ms != null && ms < LATENCY_WARN_MS) {
        Triple("#072A21", "#00E5A4", "#00E5A4")
    } else {
        Triple("#2A1714", "#FF6B5B", "#FF6B5B")
    }

    Row(
        Modifier
            .padding(leftRight = 8.px, topBottom = 3.px)
            .sBorderRadius(20)
            .styleModifier { property("background", bg) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            Modifier
                .width(6.px).height(6.px).borderRadius(50.percent)
                .styleModifier {
                    property("background", dot)
                    property("box-shadow", "0 0 0 3px ${dot}30")
                }
        )
        Box(Modifier.width(5.px))
        SpanText(
            label,
            Modifier.styleModifier {
                property("font-size", "10.5px")
                property("font-weight", "600")
                property("color", fg)
            }
        )
    }
}

private suspend fun pingHealth(url: String): LatencyState {
    return try {
        val t0 = window.asDynamic().performance.now() as Double
        val resp = window.fetch(url).await()
        val t1 = window.asDynamic().performance.now() as Double
        val ms = (t1 - t0).toInt()
        LatencyState(ms = ms, ok = resp.ok as Boolean)
    } catch (_: Throwable) {
        LatencyState(ms = null, ok = false)
    }
}

// ── Environment badge ────────────────────────────────────────────────────────

@Composable
private fun EnvBadge() {
    val label = when (AppConfig.environment) {
        Environment.PRODUCTION -> "prod"
        Environment.DEVELOPMENT -> "dev"
    }
    Box(
        Modifier
            .padding(leftRight = 7.px, topBottom = 3.px)
            .sBorderRadius(5)
            .styleModifier {
                property("background", "#101828")
                property("border", "1px solid rgba(255,255,255,.08)")
            }
    ) {
        SpanText(
            label,
            Modifier.styleModifier {
                property("font-family", "'JetBrains Mono', monospace")
                property("font-size", "10.5px")
                property("font-weight", "500")
                property("color", "#A7ADB9")
                property("letter-spacing", "-0.01em")
            }
        )
    }
}

// ── User chip + dropdown ────────────────────────────────────────────────────

@Composable
private fun UserChip(name: String, role: String, onClick: () -> Unit) {
    Row(
        Modifier
            .sPadding(5, 6)
            .sBorderRadius(999)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", "#0B1220")
                property("border", "1px solid rgba(255,255,255,0.06)")
                property("box-shadow", "0 1px 2px rgba(0,0,0,0.04)")
                property("padding-right", "14px")
            }
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            Modifier
                .width(28.px).height(28.px)
                .borderRadius(50.percent)
                .styleModifier { property("background", AppColors.AvatarGradient) },
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                name.firstOrNull()?.uppercaseChar()?.toString() ?: "A",
                Modifier.styleModifier {
                    property("font-size", "11px")
                    property("font-weight", "600")
                    property("color", "#FFFFFF")
                }
            )
        }
        Box(Modifier.width(10.px))
        Column {
            SpanText(name, Modifier.bodySMedium()
                .styleModifier { property("font-weight", "600"); property("color", "#F0F4FC") })
            Box(Modifier.height(2.px))
            SpanText(role, Modifier.bodySMedium()
                .styleModifier { property("font-size", "11px"); property("color", "#868A91") })
        }
        Box(Modifier.width(10.px))
        SpanText("▾", Modifier.fontSize(10.px).styleModifier { property("color", "#868A91") })
    }
}

@Composable
private fun UserMenu(
    onSettings: () -> Unit,
    onLogout: () -> Unit,
    settingsLabel: String,
    logoutLabel: String
) {
    Box(
        Modifier
            .position(Position.Absolute)
            .top(52.px)
            .right(0.px)
            .width(220.px)
            .sBorderRadius(14)
            .styleModifier {
                property("background", "#0B1220")
                property("border", "1px solid rgba(255,255,255,0.06)")
                property("box-shadow", "0 16px 40px rgba(0,0,0,0.10), 0 4px 12px rgba(0,0,0,0.04)")
                property("z-index", "200")
            }
            .sPadding(8, 0)
    ) {
        Column(Modifier.fillMaxWidth()) {
            Box(Modifier.fillMaxWidth().padding(leftRight = 16.px, topBottom = 8.px)) {
                SpanText(
                    "Аккаунт",
                    Modifier.bodySMedium()
                        .styleModifier {
                            property("text-transform", "uppercase")
                            property("letter-spacing", "0.06em")
                            property("font-size", "9.5px")
                            property("color", "#868A91")
                        }
                )
            }
            MenuRow(settingsLabel, "settings", onSettings)
            Box(
                Modifier.fillMaxWidth().height(1.px).margin(topBottom = 6.px)
                    .styleModifier { property("background", "#141C29") }
            )
            MenuRow(logoutLabel, "log-out", onLogout, danger = true)
        }
    }
}

@Composable
private fun MenuRow(label: String, icon: String, onClick: () -> Unit, danger: Boolean = false) {
    val fg = if (danger) "#FF6B5B" else "#F0F4FC"
    Row(
        Modifier
            .fillMaxWidth()
            .sPadding(10, 16)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier { property("transition", "background 120ms ease") },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            label,
            Modifier.bodyMMedium().styleModifier { property("color", fg) }
        )
    }
}
